import { createContext, FC, useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  deleteOrganizationById,
  getAllObjectiveTypes,
  getAllOrganizations,
  getAllPaises,
  getAllPlanTypes,
  postOrganization,
  putOrganizationById,
} from "../../api/broker";
import { iOrganizationContextProps } from "../../interfaces/SuperAdmin/ContextProps/iOrganizationContextProps";
import { iDataPaises } from "../../interfaces/SuperAdmin/Others/iDataPaises";
import { iDataTiposPlan } from "../../interfaces/SuperAdmin/Others/iDataTipoPlanes";
import {
  iDataOrganizacion,
  PostOrgFields,
  PutOrgFields,
} from "../../interfaces/SuperAdmin/Pages/iDataOrganizacion";
import { iTipoObjetivo } from "../../interfaces/SuperAdmin/Others/iDataTipoObjetivos";

const OrganizationContext = createContext<iOrganizationContextProps>(
  {} as iOrganizationContextProps
);

interface Props {
  children: React.ReactNode;
}

const OrganizationProvider: FC<Props> = ({ children }) => {
  const [orgs, setOrgs] = useState<iDataOrganizacion[] | []>([]);
  const [selectedOrg, setSelectedOrg] = useState<iDataOrganizacion | null>(
    null
  );
  const [filteredOrgs, setFilteredOrgs] = useState<iDataOrganizacion[]>([]);

  const [paises, setPaises] = useState<iDataPaises[]>([]);
  const [tiposPlan, setTiposPlan] = useState<iDataTiposPlan[]>([]);
  const [tiposObjetivo, setTiposObjetivo] = useState<iTipoObjetivo[]>([]);

  const [isLoadingOrgs, setIsLoadingOrgs] = useState(true);
  const [isLoadingPaises, setIsLoadingPaises] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [toDeleteOrg, setToDeleteOrg] = useState("");

  const [searchQuery, setSearchQuery] = useState(""); // Para manejar la búsqueda
  const [page, setPage] = useState(0); // Página actual
  const [rowsPerPage, setRowsPerPage] = useState(10); // Filas por página

  //Hook que se ejecuta al inicio cuando se monta la aplicación en el dom
  useEffect(() => {
    const load = async () => {
      try {
        const [orgRes, objRes] = await Promise.all([
          getAllOrganizations(),
          getAllObjectiveTypes(),
        ]);

        if (orgRes.data.statusCode == 200) {
          const organizacionesOrdenadas = orgRes.data.body.sort((a, b) =>
            b.updated_at.localeCompare(a.updated_at)
          ); // Ordenar por fecha descendente usando localeCompare
          setOrgs(organizacionesOrdenadas);
        } else {
          toast.error("Error al obtener las organizaciones");
        }

        if (objRes.data.statusCode === 200) {
          console.log(objRes);
          setTiposObjetivo(objRes.data.body);
        } else {
          toast.error("Error al obtener tipos de objetivo");
        }
      } catch (error) {
        console.log(error);
        toast.error("Error al obtener las organizaciones");
      } finally {
        setIsLoadingOrgs(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (orgs.length > 0) {
      setFilteredOrgs(
        orgs
          .filter(
            (o) =>
              o.nombre?.toLowerCase().includes(searchQuery.toLowerCase()) ||
              o.pais?.toLowerCase().includes(searchQuery.toLowerCase())
          )
          .sort((a, b) => a.nombre.localeCompare(b.nombre)) // Ordenar alfabéticamente por nombre
      );
    }
  }, [orgs, searchQuery]);

  const fetchPaises = async () => {
    setIsLoadingPaises(true);
    try {
      const [tiposPlanRes, paisRes] = await Promise.all([
        getAllPlanTypes(),
        getAllPaises(),
      ]);

      if (tiposPlanRes.data.statusCode === 200) {
        setTiposPlan(tiposPlanRes.data.body);
      } else {
        toast.error("Error al obtener tipos de plan");
      }

      if (paisRes.data.statusCode === 200) {
        setPaises(paisRes.data.body);
      } else {
        toast.error("Error al obtener paises");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al obtener tipos de plan y/o paises");
    } finally {
      setIsLoadingPaises(false);
    }
  };

  const toggleCreateModal = (isOpen: boolean) => {
    if (isOpen && !paises.length) {
      fetchPaises();
    }
    setIsCreateModalOpen(isOpen);
  };

  const toggleEditModal = (isOpen: boolean) => {
    if (isOpen && !paises.length) {
      fetchPaises();
    }
    if (!isOpen) {
      setSelectedOrg(null);
    }
    setIsEditModalOpen(isOpen);
  };

  const handleEdit = (org: iDataOrganizacion) => {
    setSelectedOrg(org);
    toggleEditModal(true);
  };

  //Submit del form organización
  const submitOrganizacion = async (data: PostOrgFields) => {
    setIsCreating(true);
    try {
      const res = await postOrganization(data);
      if (res.data.statusCode === 201) {
        setOrgs((prev) => [...prev, res.data.body]);
        toast.success("Organización creada exitosamente.");
        toggleCreateModal(false);
      } else {
        toast.error("Error al crear la organización.");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al crear la organización.");
    } finally {
      setIsCreating(false);
    }
  };

  //Editar organizacion
  const orgEdit = async (id_organizacion: string, data: PutOrgFields) => {
    setIsEditing(true);
    try {
      const res = await putOrganizationById(id_organizacion, data);
      if (res.data.statusCode === 200) {
        setOrgs((prev) =>
          prev.map((org) => {
            if (org.id_organizacion === id_organizacion) {
              return res.data.body;
            }
            return org;
          })
        );
        toggleEditModal(false);
        toast.success("Organización editada exitosamente.");
      } else {
        toast.error("Error al editar la organización.");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al editar la organización.");
    } finally {
      setIsEditing(false);
    }
  };

  const assignToDeleteOrg = (org: string) => {
    setToDeleteOrg(org);
  };

  //Se ejcuta desde la tabla al eliminar una organizacion
  const handleDelete = async () => {
    if (toDeleteOrg) {
      setIsDeleting(true);
      try {
        const response = await deleteOrganizationById(toDeleteOrg);
        console.log({ response });
        if (response.data.statusCode === 200) {
          // Actualizar estado eliminando la organización
          setOrgs((prev) =>
            prev.filter((org) => org.id_organizacion !== toDeleteOrg)
          );
          toast.success("Organización eliminada exitosamente.");
          setToDeleteOrg("");
        } else {
          console.error("Error al eliminar la organización");
          toast.error("Error al eliminar la organización.");
        }
      } catch (error) {
        console.error("Error al eliminar la organización", error);
        toast.error("Error al eliminar la organización");
      } finally {
        setIsDeleting(false);
      }
    }
  };

  //Se ejecuta cuando el input busqueda cambia
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setPage(0); // Resetear a la primera página al buscar
  };

  //Se ejecuta al cambiar de pagna en el paginador
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  //Se ejecuta al cambiar la cantidad de filas por pagina en el paginador
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <OrganizationContext.Provider
      value={{
        orgs,
        selectedOrg,
        filteredOrgs,
        paises,
        tiposPlan,
        tiposObjetivo,
        isLoadingOrgs,
        isLoadingPaises,
        isCreateModalOpen,
        isEditModalOpen,
        isCreating,
        isEditing,
        isDeleting,
        searchQuery,
        page,
        rowsPerPage,
        toDeleteOrg,
        toggleCreateModal,
        toggleEditModal,
        assignToDeleteOrg,
        handleDelete,
        handleEdit,
        orgEdit,
        submitOrganizacion,
        handleSearchChange,
        handleChangePage,
        handleChangeRowsPerPage,
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
};

const useSuperAdminOrganization = () => useContext(OrganizationContext);

export { OrganizationProvider, useSuperAdminOrganization };
